/**
 * useProperty — TanStack Query hook over
 * `GET /organizations/:slug/properties/:id`.
 *
 * Same closed-status union pattern as `useProperties` (loading /
 * ready / forbidden / not-found / error). The not-found branch is
 * additional vs. the list hook — a stale URL pasted into a chat
 * needs a clean "Property not found" surface, not an error retry.
 *
 * The hook keys by `(slug, id)` so two properties' detail pages keep
 * independent cache entries, and `placeholderData: keepPreviousData`
 * prevents the page from flashing into a loading state on a status
 * mutation's refetch.
 */

import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { apiClient } from '../../../shared/api-client.js';
import { useCurrentOrg } from '../../../shared/hooks/useCurrentOrg.js';
import { projectPropertyDto } from './dto-mapper.js';
import type { PropertyView } from './types.js';
import { propertyQueryKeys } from './queryKeys.js';

const FORBIDDEN = Symbol('property-detail:forbidden');
const NOT_FOUND = Symbol('property-detail:not-found');

export type UsePropertyResult =
  | { readonly status: 'loading'; readonly data?: undefined; readonly refetch: () => void }
  | { readonly status: 'ready'; readonly data: PropertyView; readonly refetch: () => void }
  | { readonly status: 'forbidden'; readonly data?: undefined; readonly refetch: () => void }
  | { readonly status: 'not-found'; readonly data?: undefined; readonly refetch: () => void }
  | { readonly status: 'error'; readonly data?: undefined; readonly refetch: () => void };

export function useProperty(id: string | undefined): UsePropertyResult {
  const orgState = useCurrentOrg();
  const orgSlug = orgState?.membership.organizationSlug ?? null;

  const query = useQuery({
    queryKey: [...propertyQueryKeys.detail(id ?? ''), orgSlug] as const,
    enabled: orgSlug !== null && id !== undefined && id.length > 0,
    retry: false,
    placeholderData: keepPreviousData,
    queryFn: async () => {
      if (id === undefined) throw new Error('useProperty called without id');
      const result = await apiClient.GET(
        '/organizations/{slug}/properties/{id}',
        {
          params: {
            path: { slug: orgSlug as string, id },
          },
        },
      );
      if (result.response.status === 403) return FORBIDDEN;
      if (result.response.status === 404) return NOT_FOUND;
      if (!result.response.ok || result.data === undefined) {
        throw new Error(
          `GET /organizations/${orgSlug}/properties/${id} failed with status ${result.response.status}`,
        );
      }
      return result.data;
    },
  });

  const refetch = (): void => {
    void query.refetch();
  };

  if (orgSlug === null || id === undefined || id.length === 0 || query.isPending) {
    return { status: 'loading', refetch };
  }
  if (query.isError) {
    return { status: 'error', refetch };
  }
  if (query.data === FORBIDDEN) {
    return { status: 'forbidden', refetch };
  }
  if (query.data === NOT_FOUND) {
    return { status: 'not-found', refetch };
  }

  return {
    status: 'ready',
    data: projectPropertyDto(query.data),
    refetch,
  };
}
